import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import Copyright from '../Copyright';
import { setResetApiAuth, userApi } from '../../api/openapi-axios-client';
import { callApi } from '../../api/helpers';
import { Session } from '../../types/api-client-types';
import { UserSession } from '../../types/auth';

import RootContext from '../../services/context-states/root-context';
import AuthService from '../../services/auth';

const IntegrationSession: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [hasToken, setHasToken] = useState<boolean>(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  const {tokenContext, userContext} = useContext(RootContext);
  const {setToken} = tokenContext;
  const {setCurrentUser} = userContext

  const [hasError, setHasError] = useState<boolean>(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setHasToken(true);
      setSessionToken(tokenParam);
    } else {
      setHasToken(false);
      setHasError(true);
    }
  }, [searchParams]);

  useEffect(() => {

    const handleLogin = async (sessionToken: string) => {
      const data = await callApi<Session>(() => userApi.account.authorize(
        {
          email: 'integrator@futuregen.com',
          password: sessionToken,
          audience: 'user-web-app',
          loginProvider: 'integrator'
        }, 
        'user'
      ));

      if (data?.token) {
        const sessionData: UserSession = {
          ...data,
          expiry: new Date().getTime() + 5000,
          SessionAudience: 'user-web-app'
        };

        localStorage.setItem('user', JSON.stringify(sessionData));

        setToken(data.token);

        const decodedToken : UserSession | null = AuthService.decodeUserToken(data.token);

        if (decodedToken?.userData) {
            setCurrentUser(decodedToken.userData);
            setResetApiAuth();
            navigate('/home');
        }
    }}

    if(hasToken && sessionToken) {
        handleLogin(sessionToken);
    }
  }, [hasToken, sessionToken]);

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 5, md: 10 }, px: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 600 }}>
          <Paper
            elevation={isMobile ? 0 : 3}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              backgroundColor: { xs: 'transparent', md: 'background.paper' },
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight: 300,
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              {hasError ? (
                <ErrorOutlineIcon sx={{ fontSize: 60, color: 'error.main' }} />
              ) : (
                <CircularProgress size={60} thickness={4} />
              )}
            </Box>
            <Typography
              component="h5"
              variant="h5"
              sx={{
                fontWeight: 900,
                fontSize: { xs: 24, sm: 28, md: 32 },
                textAlign: 'center',
                mb: 2,
              }}
            >
              {hasError ? 'Login Failed' : 'Signing you in...' }
            </Typography>

            <Copyright sx={{ mt: 4 }} />
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default IntegrationSession;
